import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store'; // Import your Vuex store
import baseRoutes from './baseRoutes'; // Import your base routes
import { RouterView } from 'vue-router';
import axios from 'axios';

// 1) Only map real routes (that have components), and strip leading slash
const branchRoutes = baseRoutes
  .filter(r => r.component)   // skip redirects or catch-alls
  .map(r => ({
    path: r.path.startsWith('/') ? r.path.slice(1) : r.path,
    name: `Branch${r.name}`,
    component: r.component,
    meta: { ...r.meta, branchScoped: true }
  }));

// 2) Wrap under /branch/:branchSlug, add default redirect child
const branchMount = {
  path: '/branch/:branchSlug',
  component: RouterView,
  children: [
    {
      path: '',
      redirect: to => `/branch/${to.params.branchSlug}/login`
    },
    ...branchRoutes
  ]
};


// 2) Pull in all baseRoutes at the root
const routes = [
  { path: '/', redirect: '/login' },

  // global copies:
  ...baseRoutes,

  // branch copies:
  branchMount,

  // fallback:
  { path: '/:catchAll(.*)', redirect: '/login' }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.afterEach((to) => {
  if (to.meta.title) {
    document.title = typeof to.meta.title === 'function' ? to.meta.title(to) : to.meta.title;
  }
});

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token');
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const allowedRoles = to.meta.roles;

  // 1) Grab branchSlug from the path if present
  const routeBranch = to.params.branchSlug || null;

  // 2) Fallback to subdomain logic if no branchSlug in path
  const host = window.location.hostname;
  const parts = host.split('.');
  const baseParts = process.env.VUE_APP_BASE_DOMAIN?.split('.') || ['jamlms','co','uk'];
  const subdomainParts = parts.slice(0, parts.length - baseParts.length);
  const hasBranchSubdomain = subdomainParts.length === 2;
  const instanceUrl = hasBranchSubdomain
    ? subdomainParts[1]
    : subdomainParts[0];
  const branchUrl = routeBranch
    ? routeBranch
    : hasBranchSubdomain
      ? subdomainParts[0]
      : null;

  try {
    // Fetch instance if missing
    if (!store.state.instance && instanceUrl) {
      await store.dispatch('fetchInstance', instanceUrl);
    }

    // Fetch branch if applicable and missing
    if (branchUrl && !store.state.branch) {
      await store.dispatch('fetchBranch', branchUrl);
    }
  } catch (error) {
    console.error('Failed to fetch instance or branch:', error);
  }

  // ---- AUTH & REDIRECTION ----

  // a) Already logged in but trying to hit login page?
  //    Send them to the right dashboard URL (branch or global).
  const isLoginRoute = to.path === '/login'
    || to.name === 'BranchLogin';
  if (isLoginRoute && token) {
    try {
      if (!store.getters.isAuthenticated) {
        await store.dispatch('fetchUser');
      }
      const userRole = store.getters.getUserRole;
      let dashPath;

      if (branchUrl) {
        dashPath = userRole === 'learner'
          ? `/branch/${branchUrl}/learner/dashboard`
          : `/branch/${branchUrl}/dashboard`;
      } else {
        dashPath = userRole === 'learner'
          ? '/learner/dashboard'
          : '/dashboard';
      }

      return next(dashPath);
    } catch (error) {
      localStorage.removeItem('token');
      return next(branchUrl
        ? `/branch/${branchUrl}/login`
        : '/login');
    }
  }

  // b) Needs auth but no token → redirect to login
  if (requiresAuth && !token) {
    localStorage.setItem('intendedRoute', to.fullPath);
    return next(branchUrl
      ? `/branch/${branchUrl}/login`
      : '/login');
  }

  // c) Only fetch the user when the route truly requires auth
  if (requiresAuth && token && !store.getters.isAuthenticated) {
    try {
      await store.dispatch('fetchUser');
    } catch (e) {
      // If fetchUser fails, clear token and send to login
      localStorage.removeItem('token');
      return next(branchUrl
        ? `/branch/${branchUrl}/login`
        : '/login');
    }
  }


  if (
  routeBranch &&
  store.getters.isAuthenticated &&
  to.name !== 'BranchLogin' &&
  to.path !== `/branch/${routeBranch}/login`
) {
  try {
    const res = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
      query: `
        query($url: String!) {
          branch(url: $url) {
            id
            is_user_in_branch
          }
        }
      `,
      variables: { url: routeBranch },
    });

    const branchInfo = res.data?.data?.branch;
    const isInBranch = branchInfo?.is_user_in_branch;

    if (!isInBranch) {
      console.warn('User is not assigned to this branch. Forcing logout.');
      await axios.post(`${process.env.VUE_APP_API_URL}/api/logout`).catch(() => {});
      localStorage.removeItem('token');
      return next(`/branch/${routeBranch}/login`);
    }
  } catch (error) {
    console.error('Error verifying branch membership:', error);
    await axios.post(`${process.env.VUE_APP_API_URL}/api/logout`).catch(() => {});
    localStorage.removeItem('token');
    return next(`/branch/${routeBranch}/login`);
  }
}

  // d) If landing on the global dashboard path as a learner, bounce
  if (to.path === '/dashboard' && store.getters.getUserRole === 'learner') {
    return next(branchUrl
      ? `/branch/${branchUrl}/dashboard`
      : '/learner/dashboard');
  }

  // e) Role restriction
  if (requiresAuth && allowedRoles && !allowedRoles.includes(store.getters.getUserRole)) {
    return next(branchUrl
      ? `/branch/${branchUrl}/login`
      : '/login');
  }

  // All good!
  next();
});

export default router;
